// material-ui
import { Alert, Box, Button, CircularProgress, FormControl, FormHelperText, FormLabel, Input, MenuItem, Select, Skeleton, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';

import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

// project import
import MainCard from 'components/MainCard';
import React from 'react';
import { deleteRequest, getRequest, postRequest } from 'utils/apiRequest';

import DeleteTwoToneIcon from '@mui/icons-material/DeleteTwoTone';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import { LoadingButton } from '@mui/lab';
import { Formik } from 'formik';
import { getPickupTimes } from 'utils/functions';
import * as Yup from 'yup';
import countries from './../../data/countries.json';
import states from './../../data/states.json';
import { UserAuthContext } from 'context/UserAuthContext';
// ==============================|| SAMPLE PAGE ||============================== //

const WarehouseSchema = Yup.object().shape({
    name: Yup.string().max(15).required('Warehouse is required'),
    line_1: Yup.string().max(75).required('Address 1 is required'),
    city: Yup.string().max(75).required('City is required'),
    state: Yup.string().max(75).required('State is required'),
    zip: Yup.string().max(75).required('Zip Code is required'),
    country: Yup.string().max(75).required('Country is required'),
})

const Row = ({ children }) => {
    return <Box sx={{ mb: 1 }}>{children}</Box>
}

const AddEditNewWarehouse = ({ warehouse = null, onSuccess = () => { }, onCancel = () => { } }) => {
    const [warehouses, setWarehouses] = React.useState([]);
    const [success, setSuccess] = React.useState(false);
    const [loading, setLoading] = React.useState(false);

    const getWarehouses = () => {
        setLoading(true);
        getRequest('/account/warehouses').then((d) => {
            setWarehouses(d);
        }).finally(() => {
            setLoading(false);
        })
    }

    const handleDeleteWarehouse = (warehouseId) => {
        var result = window.confirm("Are you sure you want to delete this?");
        if (result === true) {
            deleteRequest('/account/warehouse/delete/' + warehouse.id).then(data => {
                getWarehouses();
                window.location.reload();
            })
        }
    }
    return <Formik
        initialValues={{
            name: warehouse?.name ?? '',
            line_1: warehouse?.address?.line_1 ?? '',
            line_2: warehouse?.address?.line_2 ?? '',
            line_3: warehouse?.address?.line_3 ?? '',
            city: warehouse?.address?.city ?? '',
            state: warehouse?.address?.state ?? '',
            zip: warehouse?.address?.zip ?? '',
            country: warehouse?.address?.country_code ?? '',
            submit: null
        }}
        validationSchema={WarehouseSchema}
        onSubmit={(values, { setErrors, setStatus, setSubmitting }) => {
            setSuccess(false);
            setSubmitting(true);
            let formData = new FormData();
            Object.keys(values).forEach((k) => {
                formData.append(k, values[k]);
            })
            let url = '/account/warehouse/create';
            if (warehouse && warehouse.id) {
                url = '/account/warehouse/update';
                formData.append('id', warehouse.id)
            }
            postRequest(url, formData).then(d => {
                if (d.errors) {
                    const err = {}
                    Object.keys(d.errors).forEach((k) => {
                        err[k] = d.errors[k][0];
                    })
                    setErrors(err);
                } else {
                    setSuccess(true);
                    onSuccess(d);
                }
            }).catch(err => {
                console.log(err)
            }).finally(() => {
                setSubmitting(false);
            })
        }}>
        {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
            <form noValidate onSubmit={handleSubmit}>
                {success && (
                    <Alert severity="success">Warehouse added</Alert>
                )}
                <Row>
                    <Box sx={{ fontWeight: 'bold' }}>Warehouse Name</Box>
                    <TextField fullWidth name="name" type="text" value={values.name} onChange={handleChange} autoComplete="warehouse-name" />
                    {touched.name && errors.name && (
                        <FormHelperText error>
                            {errors.name}
                        </FormHelperText>
                    )}
                </Row>
                <Row>
                    <Box sx={{ fontWeight: 'bold' }}>Address Line 1</Box>
                    <TextField fullWidth name="line_1" type="text" value={values.line_1} onChange={handleChange} autoComplete="address-line1" />
                    {touched.line_1 && errors.line_1 && (
                        <FormHelperText error>
                            {errors.line_1}
                        </FormHelperText>
                    )}
                </Row>
                <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: '1fr 1fr' }}>
                    <Row>
                        <Box sx={{ fontWeight: 'bold' }}>Address Line 2</Box>
                        <TextField name="line_2" type="text" value={values.line_2} onChange={handleChange} autoComplete="address-line2" />
                        {touched.line_2 && errors.line_2 && (
                            <FormHelperText error>
                                {errors.line_2}
                            </FormHelperText>
                        )}
                    </Row>
                    <Row>
                        <Box sx={{ fontWeight: 'bold' }}>Address Line 3</Box>
                        <TextField name="line_3" type="text" value={values.line_3} onChange={handleChange} autoComplete="address-line3" />
                        {touched.line_3 && errors.line_3 && (
                            <FormHelperText error>
                                {errors.line_3}
                            </FormHelperText>
                        )}
                    </Row>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                    <Row>
                        <Box sx={{ fontWeight: 'bold' }}>City</Box>
                        <TextField name="city" type="text" value={values.city} onChange={handleChange} autoComplete="address-level2" />
                        {touched.city && errors.city && (
                            <FormHelperText error>
                                {errors.city}
                            </FormHelperText>
                        )}
                    </Row>
                    <Row>
                        <Box sx={{ fontWeight: 'bold' }}>State</Box>

                        <Select
                            fullWidth
                            label="State"
                            name="state"
                            value={values.state}
                            onChange={handleChange}
                            autoComplete="address-level1">
                            {states.map((s, i) => {
                                return <MenuItem key={i} value={s.code}>{s.name}</MenuItem>
                            })}
                        </Select>
                        {/* <Input name="state" type="text" value={values.state} onChange={handleChange} autoComplete="address-level1" /> */}
                        {touched.state && errors.state && (
                            <FormHelperText error>
                                {errors.state}
                            </FormHelperText>
                        )}
                    </Row>
                    <Row>
                        <Box sx={{ fontWeight: 'bold' }}>Zip Code</Box>
                        <TextField name="zip" type="text" value={values.zip} onChange={handleChange} autoComplete="postal-code" />
                        {touched.zip && errors.zip && (
                            <FormHelperText error>
                                {errors.zip}
                            </FormHelperText>
                        )}
                    </Row>
                    <Row>
                        <Box sx={{ fontWeight: 'bold' }}>Country</Box>
                        <Select
                            fullWidth
                            label="Country"
                            name="country"
                            value={values.country}
                            onChange={handleChange}
                            autoComplete="country">
                            {countries.map((c, i) => {
                                return <MenuItem key={i} value={c.code}>{c.name}</MenuItem>
                            })}
                        </Select>
                        {/* <Input name="country" type="text" value={values.country} onChange={handleChange} autoComplete="country" /> */}
                        {touched.country && errors.country && (
                            <FormHelperText error>
                                {errors.country}
                            </FormHelperText>
                        )}
                    </Row>
                </Box>
                <DialogActions sx={{ justifyContent: 'space-between' }}>
                    <Button
                        size="large"
                        onClick={() => handleDeleteWarehouse(warehouse.id)}
                        variant="outlined"
                        color="error">
                        <DeleteTwoToneIcon />
                    </Button>
                    <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: '1fr 1fr' }}>
                        <Button
                            size="large"
                            onClick={() => onCancel()}
                            variant="outlined">
                            Close
                        </Button>
                        <LoadingButton
                            disableElevation
                            disabled={isSubmitting}
                            size="large"
                            type="submit"
                            variant="contained"
                            color="primary"
                            loading={isSubmitting}
                        >
                            Submit
                        </LoadingButton>
                    </Box>
                </DialogActions>
            </form>
        )}
    </Formik>
}
const WarehousesLoading = () => {
    return (
        [...Array(5)].map((v, i) => {
            return (
                <TableRow key={i}>
                    <TableCell width={300}>
                        <Skeleton variant="text" width="100%" />
                        <Skeleton variant="text" width="60%" />
                        <Skeleton variant="text" width="40%" />
                        <Skeleton variant="text" width="40%" />
                    </TableCell>
                    <TableCell />
                </TableRow>
            )
        })
    )
}


const Warehouses = () => {
    const { getSubscriptionName } = React.useContext(UserAuthContext);
    const [accountWarehouses, setAccountWarehouses] = React.useState([]);
    const [addWarehouse, setAddWarehouse] = React.useState(false);
    const [showCarriersModal, setShowCarriersModal] = React.useState(false);
    const [showOperatingHoursModal, setShowOperatingHoursModal] = React.useState(false);
    const [loading, setLoading] = React.useState(true);
    const [warehouse, setWarehouse] = React.useState({});
    React.useEffect(() => {
        getWarehouses();
    }, []);
    const getWarehouses = () => {
        setLoading(true);
        getRequest('/account/warehouses').then((d) => {
            setAccountWarehouses(d);
        }).finally(() => {
            setLoading(false);
        })
    }
    const handleCarriers = (wh) => {
        setShowCarriersModal(true);
        setWarehouse(wh)
    }
    const handleCarriersClose = () => {
        setShowCarriersModal(false);
    }

    const handleOperatingHours = (wh) => {
        setShowOperatingHoursModal(true);
        setWarehouse(wh)
    }
    const handleOperatingHoursClose = () => {
        setShowOperatingHoursModal(false);
    }

    const showAddButton = () => {
        let maxWarehouses = 1;
        switch (getSubscriptionName().toLowerCase()) {
            case "standard": maxWarehouses = 2; break;
            case "premium": maxWarehouses = 5; break;
            case "enterprise": maxWarehouses = 1000; break;
        }
        if (accountWarehouses.length < maxWarehouses) {
            return <AddWarehouse
                onSuccess={(d) => {
                    setAccountWarehouses(d)
                    setAddWarehouse(false)
                    getWarehouses();
                }} />
        }
        return <></>
    }
    return (<>
        <MainCard title="Warehouses" secondary={showAddButton()}>
            <Table>
                <TableBody>
                    {loading ? <WarehousesLoading /> : <>
                        {accountWarehouses.map((w, i) => {
                            return (w.address ? (<TableRow key={i}>
                                <TableCell>
                                    {w.name}<br />
                                    {/* {w.address.line_1}<br />
                                    {w.address.line_2 ? <>{w.address.line_2}<br /></> : ''}
                                    {w.address.line_3 ? <>{w.address.line_3}<br /></> : ''} */}
                                    {w.address.city}{w.address.state ? <>, {w.address.state}</> : ''} {w.address.zip}<br />
                                    {/* {w.address.country_code}<br /> */}
                                </TableCell>
                                <TableCell align="right" width={180}>
                                    <Button id={"warehouse-operation-hours-"+i} onClick={() => handleOperatingHours(w)} color='warning' variant="outlined">
                                        Operating Hours
                                    </Button>
                                </TableCell>
                                <TableCell align="right" width={100}>
                                    <Button onClick={() => handleCarriers(w)} variant="outlined">
                                        <EditTwoToneIcon />
                                    </Button>
                                </TableCell>
                            </TableRow>) : null
                            )
                        })}</>}
                </TableBody>
            </Table>
        </MainCard>
        <Dialog
            open={showCarriersModal}
            onClose={handleCarriersClose}
            maxWidth="lg"
        >
            <DialogTitle>Edit Warehouse</DialogTitle>
            <DialogContent>
                <CarrierContent warehouse={warehouse} onSuccess={(d) => {
                    setAccountWarehouses(d)
                    setAddWarehouse(false)
                    getWarehouses()
                }} />
            </DialogContent>
        </Dialog>
        <Dialog
            open={showOperatingHoursModal}
            onClose={handleOperatingHoursClose}
            maxWidth="lg"
        >
            <DialogTitle>Edit Warehouse Operating Hours</DialogTitle>
            <DialogContent>
                <OperatingHoursContent warehouse={warehouse} />
            </DialogContent>
        </Dialog>
    </>);
}

const AddWarehouse = ({ onSuccess = () => { return } }) => {
    const [dialogOpen, setDialogOpen] = React.useState(false);

    const handleClose = () => {
        setDialogOpen(false);
    }
    return (<>
        <Button onClick={() => setDialogOpen(true)} variant="outlined">
            Add New
        </Button>
        <Dialog
            open={dialogOpen}
            onClose={handleClose}
            maxWidth='xl'
        >
            <DialogTitle>Add Warehouse</DialogTitle>
            <DialogContent>
                <AddEditNewWarehouse
                    onSuccess={onSuccess}
                    onCancel={handleClose} />
            </DialogContent>
        </Dialog>
    </>)
}

const EditWarehouse = ({ warehouse, onSuccess = () => { return } }) => {
    const [dialogOpen, setDialogOpen] = React.useState(false);

    const handleClose = () => {
        setDialogOpen(false);
    }
    return (<>
        <Button onClick={() => setDialogOpen(true)} variant="outlined">
            <EditTwoToneIcon />
        </Button>
        <Dialog
            open={dialogOpen}
            onClose={handleClose}
            maxWidth='xl'
        >
            <DialogTitle>Edit Warehouse</DialogTitle>
            <DialogContent>
                <AddEditNewWarehouse
                    warehouse={warehouse}
                    onSuccess={onSuccess}
                    onCancel={handleClose} />
            </DialogContent>
        </Dialog>
    </>)
}

const CarrierContent = ({ warehouse, onSuccess = (d) => { return; } }) => {
    const [carriers, setCarriers] = React.useState([]);
    const [accountCarriers, setAccountCarriers] = React.useState([]);
    const [ready, setReady] = React.useState(false);
    React.useEffect(() => {
        loadWarehouseCarriers();
    }, []);
    const loadWarehouseCarriers = () => {
        getWarehouseCarrierInfo().then((d) => {
            getAccountCarriers(d).then(() => {
                setReady(true)
            })
        })
    }
    const getWarehouseCarrierInfo = () => {
        return new Promise((resolve, reject) => {
            getRequest('/account/warehouse/carriers/' + warehouse.id).then(d => {
                setCarriers(d);
                resolve(d)
            });
        })
    }
    const getAccountCarriers = (existingCarriers) => {
        return getRequest('/account/carriers').then(d => {
            const missingCarriers = [];
            const carrierIds = existingCarriers.map((c, i) => c.carrier_id);
            d.forEach((c, i) => {
                if (carrierIds.indexOf(c.carrier_id) === -1) {
                    missingCarriers.push(c);
                }
            })
            setAccountCarriers(missingCarriers);
        })
    }
    const handleAddCarrier = (ac) => {
        setReady(false)
        let fd = new FormData();
        fd.append('accountId', ac.account_id);
        fd.append('warehouseId', warehouse.id);
        fd.append('carrierId', ac.carrier_id);
        fd.append('transitDelayHours', ac.carrier_transit_delay_hours);
        fd.append('carrierType', 'Parcel');
        fd.append('pickupTime', '17:00');
        postRequest('/account/warehouse/carrier/update', fd).then(d => {
            if (d.errors) {
                const err = {}
                Object.keys(d.errors).forEach((k) => {
                    err[k] = d.errors[k][0];
                })
                console.log(err)
            } else {
                // setCarriers(d);
                loadWarehouseCarriers();
            }
        });
    }
    const handleDeleteCarrier = (id) => {
        var result = window.confirm("Are you sure you want to delete this?");
        if (result === true) {
            setReady(false)
            deleteRequest('/account/warehouse/carrier/delete/' + id).then(data => {
                loadWarehouseCarriers();
            })
        }
    }
    return (<>
        {ready ? (
            <>
                <Box display="grid" gap={1} gridTemplateColumns="3fr 1fr" justifyContent="space-evenly">
                    <Box>
                        <strong>Name:</strong> {warehouse.name}<br />
                        {warehouse.address.line_1}<br />
                        {warehouse.address.line_2 && (<>{warehouse.address.line_2}<br /></>)}
                        {warehouse.address.line_3 && (<>{warehouse.address.line_2}<br /></>)}
                        {warehouse?.address?.city ? warehouse.address.city + ', ' : ''}
                        {warehouse?.address?.state ? warehouse.address.state + ' ' : ''}
                        {warehouse?.address?.zip ? warehouse.address.zip + ' ' : ''}
                        {warehouse?.address?.country ? warehouse.address.country : ''}<br />
                    </Box>
                    <Box>
                        <EditWarehouse warehouse={warehouse} onSuccess={(d) => onSuccess(d)} />
                    </Box>
                </Box>
                <hr />
                {accountCarriers.length > 0 && (<>
                    <Stack direction="row" spacing={3} sx={{ alignItems: 'center' }}>
                        <Typography variant="subtitle1">Add Carrier To Warehouse</Typography>
                        <Select
                            value={0}
                            onChange={(e) => handleAddCarrier(accountCarriers.filter((ac, i) => ac.id === e.target.value)[0])}
                            sx={{ minWidth: 150 }}
                        >
                            <MenuItem value={0}></MenuItem>
                            {accountCarriers.map((ac, i) => {
                                return <MenuItem key={i} value={ac.id}>{ac.carrier.name}</MenuItem>
                                // return (<Button onClick={() => handleAddCarrier(ac)} key={i} size="small" variant="outlined">{ac.carrier.name}</Button>);
                            })}
                        </Select>
                    </Stack>
                    <hr />
                </>)}
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Carrier</TableCell>
                            {/* <TableCell>Transit Multiplier</TableCell> */}
                            <TableCell align="center">Type</TableCell>
                            <TableCell align="center">Pickup Time</TableCell>
                            <TableCell sm={.5}>&nbsp;</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {carriers.length > 0 && (<>
                            {carriers.map((c, i) => {
                                return (<CarrierRow
                                    accountWarehouseCarrier={c}
                                    key={i}
                                    id={i}
                                    onSuccess={() => loadWarehouseCarriers()}
                                    onDelete={() => handleDeleteCarrier(c.id)}
                                />)
                            })}
                        </>)}
                    </TableBody>
                </Table>

            </>) : <Box sx={{ justifyContent: "center", display: 'flex' }}><CircularProgress /></Box>}
    </>
    )
}

const CarrierRow = ({ accountWarehouseCarrier, id, onSuccess = () => { return; }, onDelete = () => { return; } }) => {

    return (<TableRow>
        <TableCell>{accountWarehouseCarrier.account_carrier.carrier.name}</TableCell>
        <TableCell align="center">{accountWarehouseCarrier.carrier_type}</TableCell>
        <TableCell align="center">{accountWarehouseCarrier.pickup_time.substring(0, 5)}</TableCell>
        <TableCell align="right">
            <CarrierEdit
                accountWarehouseCarrier={accountWarehouseCarrier}
                id={id}
                onSuccess={onSuccess}
                onDelete={onDelete} />
        </TableCell>
    </TableRow>)
};



const times = getPickupTimes();

const CarrierSchema = Yup.object().shape({
    transitDelayHours: Yup.number().min(0).max(730).required('Transit Delay is required'),
    accountId: Yup.number().min(0).required('Account is required'),
    carrierId: Yup.number().min(0).required('Carrier is required'),
    warehouseId: Yup.number().min(0).required('Warehouse is required'),
    carrierType: Yup.mixed().oneOf(['Parcel', 'Freight', 'Van', 'Driver']).required('Carrier type is required'),
    pickupTime: Yup.string().required('Pickup time is required'),
})
const CarrierEdit = ({ accountWarehouseCarrier, id, onSuccess = () => { return; }, onDelete = () => { return; } }) => {
    const [success, setSuccess] = React.useState(false);
    const [mounted, setMounted] = React.useState(false);
    const [dialogOpen, setDialogOpen] = React.useState(false);
    React.useEffect(() => {
        setMounted(true);
        return () => {
            setMounted(false);
        }
    })
    return (<>
        <Button onClick={() => setDialogOpen(true)} variant="outlined">
            <EditTwoToneIcon />
        </Button>
        <Dialog
            open={dialogOpen}
            onClose={() => setDialogOpen(false)}
        >
            <DialogTitle>Edit Carrier</DialogTitle>
            <DialogContent>
                <Formik
                    initialValues={{
                        transitDelayHours: accountWarehouseCarrier.carrier_transit_delay_hours,
                        accountId: accountWarehouseCarrier.account_id,
                        carrierId: accountWarehouseCarrier.carrier_id,
                        warehouseId: accountWarehouseCarrier.warehouse_id,
                        carrierType: accountWarehouseCarrier.carrier_type,
                        pickupTime: accountWarehouseCarrier.pickup_time === "00:00:00" ? accountWarehouseCarrier.pickup_time.substring(0, 5) : accountWarehouseCarrier.pickup_time,
                    }}
                    validationSchema={CarrierSchema}
                    onSubmit={(values, { setErrors, setStatus, setSubmitting }) => {
                        setSuccess(false);
                        setSubmitting(true);
                        let formData = new FormData();
                        Object.keys(values).forEach((k) => {
                            formData.append(k, values[k]);
                        })
                        postRequest('/account/warehouse/carrier/update', formData).then(d => {
                            if (d.errors) {
                                const err = {}
                                Object.keys(d.errors).forEach((k) => {
                                    err[k] = d.errors[k][0];
                                })
                                setErrors(err);
                            } else {
                                setSuccess(true);
                                setDialogOpen(false);
                                onSuccess()
                                setTimeout(() => {
                                    if (mounted) {
                                        setSuccess(false);
                                    }
                                }, 3000)
                            }
                        }).catch(err => {
                            console.log(err)
                        }).finally(() => {
                            setSubmitting(false);
                        })
                    }}>
                    {({ errors, handleBlur, handleChange, handleSubmit, setSubmitting, isSubmitting, touched, values }) => (
                        <form noValidate onSubmit={handleSubmit}>
                            <Box display="grid" gap={1} gridTemplateColumns="3fr 3fr 3fr 2fr 1fr">
                                <FormLabel>Carrier</FormLabel>
                                <FormLabel>Type</FormLabel>
                                <FormLabel>Pickup Time</FormLabel>
                                <Box></Box>
                                <Box></Box>
                            </Box>
                            <Box display="grid" gap={1} gridTemplateColumns="3fr 3fr 3fr 2fr 1fr">
                                <FormControl sx={{ alignSelf: 'center' }}>
                                    <Box sx={{ fontWeight: 'bold' }}>{accountWarehouseCarrier.account_carrier.carrier.name}</Box>
                                </FormControl>
                                <FormControl>
                                    <Select
                                        value={values.carrierType}
                                        name="carrierType"
                                        label="Type"
                                        sx={{ width: 120 }}
                                        onChange={handleChange}
                                    >
                                        <MenuItem value="Parcel">Parcel</MenuItem>
                                        <MenuItem value="Freight">Freight</MenuItem>
                                        <MenuItem value="Van">Van</MenuItem>
                                        <MenuItem value="Driver">Driver</MenuItem>
                                    </Select>
                                </FormControl>
                                <FormControl>
                                    <Select
                                        value={values.pickupTime}
                                        id="pickupTime"
                                        name="pickupTime"
                                        label="Pickup Time"
                                        sx={{ width: 120 }}
                                        onChange={handleChange}
                                    >
                                        {times.map((t, i) => {
                                            return <MenuItem key={i} value={t}>{t}</MenuItem>
                                        })}
                                    </Select>
                                </FormControl>
                                <FormControl sx={{ justifyContent: "right", alignSelf: "center" }}>
                                    <LoadingButton
                                        disableElevation
                                        disabled={isSubmitting}
                                        size="small"
                                        type="submit"
                                        variant="contained"
                                        color={success ? "success" : "primary"}
                                        loading={isSubmitting}
                                    >{success ? "Saved" : "Submit"}
                                    </LoadingButton>
                                </FormControl>
                                <FormControl sx={{ justifyContent: "right", alignSelf: "center" }}>
                                    <DeleteTwoToneIcon onClick={() => {
                                        setSubmitting(true);
                                        onDelete()
                                    }} />
                                </FormControl>
                            </Box>
                        </form>
                    )}
                </Formik>

            </DialogContent>
        </Dialog>
    </>)
}



const OperatingHoursSchema = Yup.object().shape({
    openTime: Yup.string().required('Pickup time is required'),
    closeTime: Yup.string().required('Pickup time is required'),
    lastFulfillmentTime: Yup.string().required('Pickup time is required'),
})
const OperatingHoursContent = ({ warehouse, onSuccess = (d) => { return; } }) => {
    const [operatingHours, setOperatingHours] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [selectedDayOfWeek, setSelectedDayOfWeek] = React.useState(null);
    React.useEffect(() => {
        loadWarehouseOperatingHours();
    }, []);
    const loadWarehouseOperatingHours = () => {
        return new Promise((resolve, reject)=>{
            // setLoading(true);
            getWarehouseOperatingHours().then((d) => {
                setOperatingHours(d)
            }).finally(() => {
                setLoading(false);
                resolve();
            })
        })
    }
    const getWarehouseOperatingHours = () => {
        return new Promise((resolve, reject) => {
            getRequest('/account/warehouse/operating-hours/' + warehouse.id).then(d => {
                resolve(d)
            });
        })
    }
    const handleDeleteOperatingHours = (id) => {
        var result = window.confirm("Are you sure you want to delete this?");
        if (result === true) {
            setLoading(true)
            deleteRequest('/account/warehouse/operating-hours/delete/' + id).then(data => {
                loadWarehouseOperatingHours();
            }).finally(() => {
                setLoading(false);
            });
        }
    }
    let currentDays = {};
    for(var i = 0; i < 7; i++){
        currentDays[i] = operatingHours.filter((oh, k) => oh.day_of_week === i).pop();
        if (currentDays[i] === undefined) {
            currentDays[i] = {
                day_of_week: i,
                warehouse_id: warehouse.id,
                open_time: '--',
                close_time: '--',
                last_fulfillment_time: '--'
            }
        }
    }
    return (<>
        {!loading ? (
            <>
                <Box>
                    <strong>Name:</strong> {warehouse.name}
                </Box>
                <hr />
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Day of Week</TableCell>
                            <TableCell align="center">Open Time</TableCell>
                            <TableCell align="center">Close Time</TableCell>
                            <TableCell>Last Fulfillment Time</TableCell>
                            <TableCell></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                            {Array(7).fill(0).map((n, j) => {
                                const currentDay = currentDays[j];
                                const editRow = selectedDayOfWeek === currentDay.day_of_week;
                                return (
                                    <TableRow key={j}>
                                        <TableCell>{(new Date((new Date()).setDate(currentDay.day_of_week))).toLocaleString('en-us', { weekday: 'long' })}</TableCell>
                                        <TableCell align="center">{currentDay.open_time}</TableCell>
                                        <TableCell align="center">{currentDay.close_time}</TableCell>
                                        <TableCell>{currentDay.last_fulfillment_time}</TableCell>
                                        <TableCell>
                                            <Button onClick={() => setSelectedDayOfWeek(currentDay.day_of_week)} variant="outlined">
                                                <EditTwoToneIcon />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                )
                            })}
                    </TableBody>
                </Table>
                <EditOperatingHours 
                    dayOfWeek={selectedDayOfWeek} 
                    operatingHours={Object.values(currentDays).filter((oh, i) => oh.day_of_week === selectedDayOfWeek).pop()} 
                    onSuccess={()=>{
                        loadWarehouseOperatingHours().then((d)=>{
                        }).finally(()=>{
                            setSelectedDayOfWeek(null)
                        })
                    }}
                    onClose={()=>{
                        setSelectedDayOfWeek(null)
                    }}
                    />
            </>) : <Box sx={{ justifyContent: "center", display: 'flex' }}><CircularProgress /></Box>}
    </>
    )
}

const EditOperatingHours = ({ dayOfWeek, operatingHours, onSuccess = () => {} , onClose = () => {} }) => {
    if (dayOfWeek < 0 || operatingHours === undefined) {
        return <></>
    }
    const dayName = (new Date((new Date()).setDate(operatingHours.day_of_week))).toLocaleString('en-us', { weekday: 'long' });
    return (
        <Dialog
            open={true}
            maxWidth="xs"
            fullWidth={false}
            onClose={()=>onClose()}
        >
            <DialogTitle>Edit Operating Hours for {dayName}</DialogTitle>
            <DialogContent>
                <Formik
                    initialValues={{
                        openTime: operatingHours.open_time.substring(0, operatingHours.open_time.lastIndexOf(":00")),
                        closeTime: operatingHours.close_time.substring(0, operatingHours.close_time.lastIndexOf(":00")),
                        lastFulfillmentTime: operatingHours.last_fulfillment_time.substring(0, operatingHours.last_fulfillment_time.lastIndexOf(":00")),
                    }}
                    validationSchema={OperatingHoursSchema}
                    onSubmit={(values, { setErrors, setStatus, setSubmitting }) => {
                        setSubmitting(true);
                        let formData = new FormData();
                        formData.append('dayOfWeek', dayOfWeek);
                        formData.append('warehouseId', operatingHours.warehouse_id);
                        Object.keys(values).forEach((k) => {
                            formData.append(k, values[k]);
                        })
                        postRequest('/account/warehouse/operating-hours/update', formData).then(d => {
                            if (d.errors) {
                                const err = {}
                                Object.keys(d.errors).forEach((k) => {
                                    err[k] = d.errors[k][0];
                                })
                                setErrors(err);
                                setSubmitting(false);
                            } else {
                                onSuccess()
                            }
                        }).catch(err => {
                            console.log(err)
                            setSubmitting(false);
                        })
                    }}>
                    {({ errors, handleBlur, handleChange, handleSubmit, setSubmitting, isSubmitting, touched, values }) => (
                        <form noValidate onSubmit={handleSubmit}>
                            <Box>
                                <Box display="grid" gap={1} gridTemplateColumns="1fr" alignItems={"end"}>
                                    <Box display="grid" gap={1} gridTemplateColumns="1fr 1fr" alignItems="center">
                                        <label>Open Time</label>
                                        <Select
                                            value={values.openTime}
                                            id="openTime"
                                            name="openTime"
                                            sx={{ width: 120 }}
                                            onChange={handleChange}
                                        >
                                            {times.map((t, i) => {
                                                return <MenuItem key={i} value={t}>{t}</MenuItem>
                                            })}
                                        </Select>
                                    </Box>
                                    <Box display="grid" gap={1} gridTemplateColumns="1fr 1fr" alignItems="center">
                                        <label>Close Time</label>
                                        <Select
                                            value={values.closeTime}
                                            id="closeTime"
                                            name="closeTime"
                                            sx={{ width: 120 }}
                                            onChange={handleChange}
                                        >
                                            {times.map((t, i) => {
                                                return <MenuItem key={i} value={t}>{t}</MenuItem>
                                            })}
                                        </Select>
                                    </Box>
                                    <Box display="grid" gap={1} gridTemplateColumns="1fr 1fr" alignItems="center">
                                        <label>Last Fulfillment Time</label>
                                        <Select
                                            value={values.lastFulfillmentTime}
                                            id="lastFulfillmentTime"
                                            name="lastFulfillmentTime"
                                            sx={{ width: 120 }}
                                            onChange={handleChange}
                                        >
                                            {times.map((t, i) => {
                                                return <MenuItem key={i} value={t}>{t}</MenuItem>
                                            })}
                                        </Select>
                                    </Box>
                                    <Box display="grid" gap={1} gridTemplateColumns="1fr" alignItems="end">
                                        
                                    <LoadingButton
                                        disableElevation
                                        disabled={isSubmitting}
                                        size="large"
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        loading={isSubmitting}>
                                        Save
                                    </LoadingButton>
                                    </Box>
                                </Box>
                            </Box>
                        </form>
                    )}
                </Formik>
            </DialogContent>
        </Dialog>
    )
}

export default Warehouses;
